import * as React from 'react';
import { getCategoriesData, getTagsDataBySlugs } from '../../api/api';
import { Category, dtoToCategoryProps } from './category';
import { FetchStatus } from '../view-rapper';
import { biLogger } from '../../bi';
import { appMarketPageView } from '@wix/bi-logger-market/v2';
import { config } from '../../config';
import type { MarketplaceStore } from '../../component-decorators/with-marketplace-store';
import { withMarketplaceStore } from '../../component-decorators/with-marketplace-store';
import type { appMarketPageViewParams } from '@wix/bi-logger-market/v2/types';
import type { IFetcherInjected } from '../../component-decorators/with-fetcher';
import { WithFetcher } from '../../component-decorators/with-fetcher';
import { composer } from '../../component-decorators/composer';
import type { InjectedExperimentsProps } from '@wix/wix-experiments-react';
import { CenteredLoader } from '../centered-loader';
import { ErrorPageType, Origin } from '../..';
import { CategorySEO } from './category-seo';
import type { TFunction } from 'i18next';
import { useConfigContext } from '../../config-context';
import { ErrorPageContainer } from '../error-page/error-page-container';
import {
  getPathForBi,
  getReferralInfo,
  getWidgetReferral,
} from '../../common/bi-events';
import { withExperiments } from '../../component-decorators/with-experiments';
import type { GetWebSolutionsByTagsResponse } from '@wix/ambassador-marketplace/types';
import type { AppMarketRpcClient } from '../../types/app-market-rpc-client';

const initialCategoryFetch = (
  slug,
  subCategories,
  experiments,
  locale,
  countryCode,
  rpcClient?: AppMarketRpcClient,
) => {
  if (subCategories && subCategories.length > 0) {
    return () =>
      Promise.all([
        getCategoriesData(locale, rpcClient),
        getTagsDataBySlugs([slug], locale, countryCode, true, true, rpcClient),
        getTagsDataBySlugs(
          subCategories,
          locale,
          countryCode,
          true,
          true,
          rpcClient,
        ),
      ]);
  }

  return () =>
    Promise.all([
      getCategoriesData(locale, rpcClient),
      getTagsDataBySlugs([slug], locale, countryCode, true, true, rpcClient),
    ]);
};

const getNameForTitle = (data, subCategories) => {
  // if we have only one sub category- it should be the title for SEO
  if (subCategories && subCategories.length === 1) {
    const subCatData = data?.[1]?.tagsAndSubCategories?.[0].subCategories.find(
      (cat) => cat.slug === subCategories[0],
    );
    return subCatData?.name;
  }
  return data?.[1]?.tagsAndSubCategories?.[0]?.tag?.name;
};

export interface ICategoryFetcherProps
  extends Partial<InjectedExperimentsProps> {
  slug: string;
  subCategories?: string[];
}

const isValidCategoryData = (fetchData) => {
  return (
    fetchData &&
    fetchData[1] &&
    fetchData[1].tagsAndSubCategories &&
    fetchData[1].tagsAndSubCategories[0]
  );
};

export const CategoryFetcher = composer()
  .withExperiments()
  .withTranslation()
  .compose(({ slug, subCategories, experiments, t }) => {
    const { locale, countryCode, rpcClient } = useConfigContext();
    return (
      <WithFetcher
        loader={<CenteredLoader />}
        initialFetch={initialCategoryFetch(
          slug,
          subCategories,
          experiments,
          locale,
          countryCode,
          rpcClient,
        )}
        fetchMemoizationId={`category-${slug}-${locale}`}
      >
        {({ fetchData, reFetch }) => {
          return (
            <CategoryContainerWrapper
              slug={slug}
              subCategories={subCategories}
              experiments={experiments}
              locale={locale}
              countryCode={countryCode}
              fetchData={fetchData}
              reFetch={reFetch}
              t={t}
            />
          );
        }}
      </WithFetcher>
    );
  });
const CategoryContainerWrapper = ({
  slug,
  subCategories,
  experiments,
  locale,
  countryCode,
  fetchData,
  reFetch,
  t,
}: any) => {
  return isValidCategoryData(fetchData) ? (
    <>
      {config.origin === Origin.STANDALONE && (
        <CategorySEO
          categoryName={getNameForTitle(fetchData, subCategories)}
          categoryDesc={fetchData[1].tagsAndSubCategories[0].tag.description}
          subCategoriesCount={subCategories ? subCategories.length : 0}
          categorySlug={slug}
        />
      )}
      <CategoryContainer
        slug={slug}
        subCategories={subCategories}
        fetchData={fetchData}
        reFetch={reFetch}
        t={t}
        locale={locale}
        countryCode={countryCode}
      />
    </>
  ) : (
    <ErrorPageContainer slug={slug} errorPageType={ErrorPageType.NOT_FOUND} />
  );
};

interface ICategoryContainerProps
  extends ICategoryFetcherProps,
    IFetcherInjected {
  marketplaceStore?: MarketplaceStore;
  t: TFunction;
  locale: string;
  countryCode: string;
}

interface ICategoryContainerState {
  isFetchingData: FetchStatus;
}

@withExperiments
@withMarketplaceStore
class CategoryContainer extends React.Component<
  ICategoryContainerProps,
  ICategoryContainerState
> {
  constructor(props: ICategoryContainerProps) {
    super(props);
    this.state = { isFetchingData: FetchStatus.FETCHING };
  }

  async componentDidMount() {
    const categoryRes = this.props.fetchData[1];
    this.sendPageViewBi(categoryRes);
  }

  async componentDidUpdate(prevProps) {
    const categoryChanged = this.props.slug !== prevProps.slug;

    const subCategoriesChanged =
      (this.props.subCategories || []).join('') !==
      (prevProps.subCategories || []).join('');

    if (categoryChanged || subCategoriesChanged) {
      if (
        subCategoriesChanged &&
        this.props.subCategories &&
        this.props.subCategories.length > 0
      ) {
        this.props.reFetch(() =>
          Promise.all([
            getCategoriesData(this.props.locale),
            getTagsDataBySlugs(
              [this.props.slug],
              this.props.locale,
              this.props.countryCode,
              true,
              true,
            ),
            getTagsDataBySlugs(
              this.props.subCategories,
              this.props.locale,
              this.props.countryCode,
              true,
              true,
            ),
          ]),
        );
      } else {
        const promises = [
          getCategoriesData(this.props.locale),
          getTagsDataBySlugs(
            [this.props.slug],
            this.props.locale,
            this.props.countryCode,
            true,
            true,
          ),
        ];
        this.props.reFetch(() => Promise.all(promises));
      }

      const categoryRes = this.props.fetchData[1];
      this.sendPageViewBi(categoryRes);
      this.setState({ isFetchingData: FetchStatus.COMPLETE });
    }
  }

  sendPageViewBi = (dto: GetWebSolutionsByTagsResponse): void => {
    const pageViewParams: appMarketPageViewParams = {
      app_id: null,
      market: config.originForBI,
      navigation_source: this.props?.marketplaceStore?.route?.searchLocation,
      // pageType: 'category-page',
      tag_name: dto.tagsAndSubCategories[0].tag.slug.replace(/-/g, '_'),
      tag_type: dto.tagsAndSubCategories[0].tag.type.toLowerCase(),
      msid: this.props.marketplaceStore.metaSiteId,
      referral_info: getReferralInfo(
        this.props.marketplaceStore.route &&
          this.props.marketplaceStore.route.referral,
        this.props.marketplaceStore.prevRoute,
      ),
      search_term:
        this.props.marketplaceStore.prevRoute &&
        this.props.marketplaceStore.prevRoute.query,
      widget_referral: getWidgetReferral(),
      utm_id: this.props?.marketplaceStore?.route?.utm_id,
      referral_tag_name: this.props?.marketplaceStore?.prevRoute?.slug?.replace(
        /-/g,
        '_',
      ),
      referral_tag_type: getPathForBi(
        this.props.marketplaceStore?.prevRoute?.path,
      ),
      referral_sub_tag_name:
        this.props.marketplaceStore?.prevRoute?.subCategories?.join(','),
      is_external:
        this.props?.marketplaceStore?.route?.referral &&
        this.props?.marketplaceStore?.route?.referral !== 'manage_apps',
    };
    biLogger.report(appMarketPageView(pageViewParams));
  };

  render() {
    const categoriesListRes = this.props.fetchData[0];
    const categoryRes = this.props.fetchData[1];
    const subCategoriesRes =
      this.props.fetchData.length === 2
        ? this.props.fetchData[1]
        : this.props.fetchData[2];
    const categoryProps = dtoToCategoryProps(
      categoryRes,
      subCategoriesRes,
      categoriesListRes,
      this.props.t,
    );
    return (
      <>
        <Category {...categoryProps} />
      </>
    );
  }
}
